import React, { useEffect, useRef, useState } from "react";
import { Box, Grid, Paper } from "@mui/material";
import { observer } from "mobx-react-lite";
import { withRouter } from "react-router-dom";

import StyledPage from "../../common/StyledPage";
import StyledSubHeader from "../../common/StyledSubHeader";
import StyledAvatarChipsTextField from "../../common/StyledAvatarChipTextField";
import StyledSwitch from "../../common/StyledSwitch";
import { StyledMultilineTextField, StyledTextField } from "../../common/StyledTextField";
import { LoadingButton, LoadingTextButton, TextButton } from "../../common/StyledButtons";
import { StyledLoadingSection } from "../../common/StyledProgressCircle";

import { isAdmin } from "../../../actions/profile/UserActions";
import { getUserId } from "../../../selectors/UserSelectors";
import {
    getAllUsersForAdmin,
    getEmailByIdForAdmin,
    getEmailForAdmin,
    saveEmailForAdmin,
    sendEmailBlastForAdmin
} from "../../../actions/internal/AdminActions";
import {
    NOTIFICATION_TYPE,
    showNotificationBar
} from "../../../actions/error-pages/NotificationBarActions";

const DEFAULT_TEMPLATE = `<table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0">
<tr><td>New Email</td></tr>
</table>`;

const AdminEditEmailPage = ({ history, match }) => {
    const emailId =
        match &&
        match.params &&
        match.params.emailId
            ? match.params.emailId
            : null;

    const formattedUsers = useRef([]);

    const [isLoading, setIsLoading] = useState(true);
    const [isSending, setIsSending] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const [subject, setSubject] = useState("");
    const [html, setHtml] = useState(DEFAULT_TEMPLATE);
    const [recipients, setRecipients] = useState([]);
    const [isBlastEmail, setIsBlastEmail] = useState(false);

    const [errors, setErrors] = useState([]);

    const loadEmailForEdit = async () => {
        try {
            const email = await getEmailByIdForAdmin(getUserId(), emailId);
            if (!email) {
                history.replace("/admin/email-marketing");
                return;
            }
            const { body, recipients, subject} = email.email_content;

            if (subject) {
                setSubject(subject);
            } else {
                setSubject("");
            }

            if (body) {
                setHtml(body);
            } else {
                setHtml(DEFAULT_TEMPLATE);
            }

            if (Array.isArray(recipients)) {
                setRecipients(recipients);
            } else {
                setRecipients([]);
            }

            setIsBlastEmail(email && email.isBlastEmail === true);
        } catch (e) {
            showNotificationBar(
                NOTIFICATION_TYPE.ERROR,
                "Could not load email for editing."
            );
        }
    };

    const loadData = async () => {
        if (!isAdmin()) {
            history.replace("/dashboard");
            return;
        }

        if (!emailId) {
            history.replace("/admin/email-marketing");
            return;
        }

        try {
            const users = await getAllUsersForAdmin(getUserId());

            const safeUsers = Array.isArray(users) ? users : [];

            formattedUsers.current = safeUsers
                .map(u => {
                    const first =
                        u &&
                        u.user_personal &&
                        u.user_personal.first_name
                            ? u.user_personal.first_name
                            : "";

                    const last =
                        u &&
                        u.user_personal &&
                        u.user_personal.last_name
                            ? u.user_personal.last_name
                            : "";

                    const email =
                        u && u.user_login_id
                            ? u.user_login_id
                            : "";

                    return {
                        name: `${first} ${last} (${email})`,
                        email: email,
                        value: u
                    };
                })
                .sort((a, b) => a.name.localeCompare(b.name));

            await loadEmailForEdit();
        } catch (e) {
            showNotificationBar(
                NOTIFICATION_TYPE.ERROR,
                "There was a problem loading page data."
            );
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const validateForm = () => {
        const errs = [];

        if (!subject || subject.trim() === "") {
            errs.push("subject");
        }

        if (!html || html.trim() === "") {
            errs.push("body");
        }

        if (!isBlastEmail && (!recipients || recipients.length === 0)) {
            errs.push("recipients");
        }

        setErrors(errs);
        return errs.length === 0;
    };

    const handleSave = async () => {
        try {
            setIsSaving(true);

            await saveEmailForAdmin(
                getUserId(),
                {
                    subject: subject,
                    body: html,
                    recipients: recipients,
                    email_id: emailId
                }
            );

            showNotificationBar(
                NOTIFICATION_TYPE.SUCCESS,
                "Email saved successfully."
            );
            history.replace(`/admin/email-marketing/${emailId}`);
        } catch (e) {
            console.error(e);
            showNotificationBar(
                NOTIFICATION_TYPE.ERROR,
                "Could not save the email."
            );
        } finally {
            setIsSaving(false);
        }
    };

    const handleSend = async () => {
        if (!validateForm()) return;

        try {
            setIsSending(true);

            await sendEmailBlastForAdmin(
                getUserId(),
                {
                    subject: subject,
                    body: html,
                    recipients: recipients,
                    email_id: emailId
                },
                { isBlastEmail: isBlastEmail }
            );

            showNotificationBar(
                NOTIFICATION_TYPE.SUCCESS,
                "Email sent successfully."
            );
            history.replace(`/admin/email-marketing/${emailId}`);
        } catch (e) {
            console.error(e);
            showNotificationBar(
                NOTIFICATION_TYPE.ERROR,
                "Could not send the email."
            );
        } finally {
            setIsSending(false);
        }
    };

    const renderTemplate = (template, data) => {
        return template.replace(/\{([\w.[\]0-9]+)\}/g, (_, key) => {
            try {
                const keys = key.split(".");
                let value = data;

                for (let i = 0; i < keys.length; i++) {
                    if (value && value[keys[i]] !== undefined) {
                        value = value[keys[i]];
                    } else {
                        return "";
                    }
                }

                return value !== null && value !== undefined ? value : "";
            } catch (e) {
                return "";
            }
        });
    };

    const previewHtml = isBlastEmail
        ? html
        : renderTemplate(
              html,
              recipients && recipients.length > 0
                  ? { user: recipients[0].value }
                  : {}
          );

    if (isLoading) {
        return <StyledLoadingSection />;
    }

    return (
        <StyledPage>
            <StyledSubHeader title="Edit Email" />

            <Grid container spacing={2} sx={{ marginTop: "24px" }}>
                <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ padding: "16px" }}>
                        <StyledAvatarChipsTextField
                            label="Recipients"
                            options={formattedUsers.current}
                            value={recipients}
                            onChange={(e, val) => setRecipients(val)}
                            error={errors.indexOf("recipients") !== -1}
                            helperText={
                                !isBlastEmail
                                    ? `${recipients.length}/50 Recipients`
                                    : null
                            }
                        />

                        <StyledSwitch
                            title="Blast Email"
                            description="Send without personalization."
                            checked={isBlastEmail}
                            onChange={e =>
                                setIsBlastEmail(e.target.checked === true)
                            }
                        />

                        <StyledTextField
                            label="Subject"
                            value={subject}
                            onChange={e => setSubject(e.target.value)}
                            error={errors.indexOf("subject") !== -1}
                        />

                        <StyledMultilineTextField
                            label="Body"
                            rows={25}
                            value={html}
                            onChange={e => setHtml(e.target.value)}
                            error={errors.indexOf("body") !== -1}
                        />
                    </Paper>
                </Grid>

                <Grid item xs={12}>
                    <h4>Preview</h4>
                    <Box
                        sx={{
                            border: "1px solid #ccc",
                            borderRadius: 1,
                            padding: 2,
                            backgroundColor: "#fafafa"
                        }}
                        dangerouslySetInnerHTML={{ __html: previewHtml }}
                    />
                </Grid>

                <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: '10px'}}>
                    <LoadingTextButton
                        disabled={isSaving || isSending}
                        isLoading={isSaving}
                        onClick={handleSave}
                    >
                        Save
                    </LoadingTextButton>
                    <LoadingButton
                        disabled={isSending || isSaving}
                        isLoading={isSending}
                        onClick={handleSend}
                    >
                        Send
                    </LoadingButton>
                </Grid>
            </Grid>
        </StyledPage>
    );
};

export default withRouter(observer(AdminEditEmailPage));
